/**
 * Menu « … » : range les actions rares d'un écran (modifier, annuler,
 * archiver) derrière un seul bouton, pour garder l'action principale lisible.
 * Une entrée fait un geste (`onSelect`) ou ouvre une page (`to`). Le bouton
 * peut porter son propre libellé (« Créer » de la colonne latérale).
 * Échap ou un appui à l'extérieur le referment ; le focus revient au bouton.
 */
import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Check, MoreHorizontal } from 'lucide-react';

interface ActionMenuEntry {
  label: string;
  icon?: ReactNode;
  tone?: 'danger';
  /** Choix parmi quelques valeurs (la vue du planning) : l'entrée cochée est la valeur. */
  checked?: boolean;
}

/** Entrées du menu que les flèches parcourent : gestes, liens et choix. */
const MENU_ITEMS = '[role="menuitem"], [role="menuitemradio"]';

export type ActionMenuItem = ActionMenuEntry &
  ({ onSelect: () => void; to?: never } | { to: string; state?: unknown; onSelect?: never });

export function ActionMenu(props: {
  label?: string;
  items: ActionMenuItem[];
  /** Bouton à libellé visible à la place des « … ». */
  trigger?: { className: string; content: ReactNode };
  /** Pictogramme du bouton à la place des « … » (même bouton, même taille). */
  icon?: ReactNode;
  /** Bouton au début de sa ligne (zone de saisie) : la liste s'ouvre vers la droite. */
  align?: 'start';
}): ReactNode {
  const [open, setOpen] = useState(false);
  // Sur téléphone le bouton est souvent à gauche : la liste s'ouvre alors vers
  // la droite au lieu de sortir de l'écran.
  const [alignStart, setAlignStart] = useState(false);
  // Bouton au milieu de l'écran (après des pastilles) : ni à gauche ni à droite la
  // liste ne tient. Elle est alors décalée pour rester entière dans l'écran.
  const [shift, setShift] = useState(0);
  // Bouton près du bas de l'écran (une zone de saisie collée au pouce) : la liste
  // s'ouvre vers le haut au lieu de sortir de l'écran.
  const [up, setUp] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  useLayoutEffect(() => {
    if (!open) {
      setShift(0);
      setUp(false);
      return;
    }
    const rect = list.current?.getBoundingClientRect();
    if (!rect) return;
    const gutter = 8;
    const anchor = trigger.current?.getBoundingClientRect();
    if (!up && anchor && rect.bottom > window.innerHeight - gutter && anchor.top - gutter > rect.height) setUp(true);
    // Largeur de la page, pas `innerWidth` : sur téléphone, une liste qui dépasse
    // élargit la zone visible et masquerait son propre débordement.
    const overRight = rect.right - (document.documentElement.clientWidth - gutter);
    const overLeft = gutter - rect.left;
    setShift(overRight > 0 ? -Math.min(overRight, Math.max(0, rect.left - gutter)) : overLeft > 0 ? overLeft : 0);
  }, [open, alignStart, up]);

  useEffect(() => {
    if (!open) return;
    // Un choix (la vue du planning) s'ouvre sur l'entrée cochée.
    (root.current?.querySelector<HTMLElement>('[aria-checked="true"]')
      ?? root.current?.querySelector<HTMLElement>(MENU_ITEMS))?.focus();
    const onPointer = (event: PointerEvent): void => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        trigger.current?.focus();
        return;
      }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      const items = [...(root.current?.querySelectorAll<HTMLElement>(MENU_ITEMS) ?? [])];
      const index = items.indexOf(document.activeElement as HTMLElement);
      const next = event.key === 'ArrowDown' ? index + 1 : index - 1;
      items[(next + items.length) % items.length]?.focus();
      event.preventDefault();
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (props.items.length === 0) return null;
  return (
    <div className="action-menu" ref={root}>
      <button
        ref={trigger}
        type="button"
        className={props.trigger?.className ?? 'btn btn--secondary btn--icon'}
        aria-label={props.label ?? 'Plus d’actions'}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => {
          const rect = trigger.current?.getBoundingClientRect();
          setAlignStart(props.align === 'start' || Boolean(rect && rect.right < 260));
          setOpen((value) => !value);
        }}
      >
        {props.trigger?.content ?? props.icon ?? <MoreHorizontal size={20} aria-hidden="true" />}
      </button>
      {open ? (
        <div
          className={`action-menu__list${alignStart ? ' action-menu__list--start' : ''}${up ? ' action-menu__list--up' : ''}`}
          role="menu"
          id={menuId}
          ref={list}
          style={{ '--menu-shift': shift ? `${shift}px` : undefined }}
        >
          {props.items.map((item) =>
            item.to !== undefined ? (
              <Link
                key={item.label}
                role="menuitem"
                to={item.to}
                state={item.state}
                className={item.tone === 'danger' ? 'is-danger' : undefined}
                onClick={() => setOpen(false)}
              >
                {item.icon}
                <span>{item.label}</span>
              </Link>
            ) : (
              <button
                key={item.label}
                type="button"
                role={item.checked === undefined ? 'menuitem' : 'menuitemradio'}
                aria-checked={item.checked}
                className={item.tone === 'danger' ? 'is-danger' : undefined}
                onClick={() => {
                  setOpen(false);
                  trigger.current?.focus();
                  item.onSelect();
                }}
              >
                {item.icon}
                <span>{item.label}</span>
                {item.checked ? <Check className="action-menu__check" size={18} aria-hidden="true" /> : null}
              </button>
            ),
          )}
        </div>
      ) : null}
    </div>
  );
}
